import { ROUTE_PERMISSIONS, CATEGORY_PRIORITY, CATEGORY_DEFAULT_ROUTES, getRoutePermissionConfig } from '../config/routePermissions';

/**
 * Check if user has access to a route based on their permissions
 */
export const hasRouteAccess = (
  pathname: string,
  userPermissions: string[],
  isSystemAdmin: boolean = false
): boolean => {
  // Normalize pathname - remove leading/trailing slashes and ensure it starts with /
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  
  // Find route config using the utility function
  const routeConfig = getRoutePermissionConfig(normalizedPath);

  // If no config found, allow access (public route or not configured)
  if (!routeConfig) {
    return true;
  }

  // Admin routes require system admin check
  if (routeConfig.category === 'admin') {
    return isSystemAdmin;
  }

  // If no permissions required, allow access
  if (routeConfig.requiredPermissions.length === 0) {
    return true;
  }

  // Check if user has ANY of the required permissions
  return routeConfig.requiredPermissions.some(perm => userPermissions.includes(perm));
};

/**
 * Find the first accessible route based on user permissions
 * Checks categories in priority order and returns the default route for the first accessible category
 * Prefers non-admin routes when user has other permissions
 * IMPORTANT: Only checks admin routes if user has no other permissions AND is system admin
 */
export const getFirstAccessibleRoute = (
  userPermissions: string[],
  isSystemAdmin: boolean = false
): string => {
  // If permissions array is empty, don't make redirect decisions yet
  // This prevents redirecting to admin routes when permissions haven't loaded
  if (userPermissions.length === 0) {
    // Return dashboard as safe fallback - component will show loading/error if needed
    return '/dashboard';
  }

  // Check if user has any non-admin permissions
  const hasNonAdminPermissions = userPermissions.some(perm => {
    // Check if this permission belongs to a non-admin category
    const routeConfig = ROUTE_PERMISSIONS.find(r => 
      r.requiredPermissions.includes(perm) && r.category !== 'admin'
    );
    return routeConfig !== undefined;
  });

  // If user has non-admin permissions, prioritize non-admin routes and exclude admin routes
  // Only check admin routes if user has NO non-admin permissions AND is system admin
  const categoriesToCheck = hasNonAdminPermissions 
    ? CATEGORY_PRIORITY.filter(cat => cat !== 'admin') // Exclude admin routes if user has other permissions
    : isSystemAdmin 
      ? CATEGORY_PRIORITY // Include admin routes only if no other permissions and is system admin
      : CATEGORY_PRIORITY.filter(cat => cat !== 'admin'); // Exclude admin routes if not system admin

  // Check each category in priority order
  for (const category of categoriesToCheck) {
    const defaultRoute = CATEGORY_DEFAULT_ROUTES[category];
    
    if (!defaultRoute) continue;
    
    // Check if user has access to this category's default route
    if (hasRouteAccess(defaultRoute, userPermissions, isSystemAdmin)) {
      return defaultRoute;
    }
  }
  
  // If no accessible route found, try profile as a last resort (might be more lenient)
  // If even profile is not accessible, the component will handle showing an error
  const profileRoute = '/profile/account';
  if (hasRouteAccess(profileRoute, userPermissions, isSystemAdmin)) {
    return profileRoute;
  }
  
  // Final fallback - return dashboard (component will handle if user doesn't have access)
  return '/dashboard';
};

/**
 * Get all accessible routes for a user
 */
export const getAccessibleRoutes = (
  userPermissions: string[],
  isSystemAdmin: boolean = false
): string[] => {
  return ROUTE_PERMISSIONS
    .filter(route => {
      if (route.category === 'admin') {
        return isSystemAdmin;
      }
      if (route.requiredPermissions.length === 0) {
        return true;
      }
      return route.requiredPermissions.some(perm => userPermissions.includes(perm));
    })
    .map(route => route.path);
};

